import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Target, Trash2, Mail, Building2, Calendar } from 'lucide-react';
import { getLeads, deleteLead } from '../lib/api';

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    try {
      const res = await getLeads();
      setLeads(res.data);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLead(id);
      setLeads(leads.filter(l => l.id !== id));
      toast.success('Lead deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="leads-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0">
              <Target className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Intelligence</p>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Leads</h1>
              <p className="text-sm text-white/50 mt-0.5">Captured leads from forms and campaigns</p>
            </div>
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Captured</p>
            <p className="text-xl font-bold text-[hsl(var(--brand))] font-mono">{leads.length}</p>
          </div>
        </div>
      </div>

      <Card className="bg-[hsl(var(--pane))] border border-white/10 animate-fade-in-up" style={{boxShadow: 'var(--shadow-elev-1)', animationDelay: '0.1s'}}>
        <CardContent className="p-0">
          {leads.length === 0 ? (
            <div className="text-center py-16">
              <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-muted-foreground">No leads captured yet</p>
              <p className="text-xs text-muted-foreground mt-1">Create forms and share them to start collecting leads</p>
            </div>
          ) : (
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map(lead => (
                  <TableRow key={lead.id} className="table-row-hover">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="font-mono text-sm">{lead.email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{lead.first_name} {lead.last_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Building2 className="w-3.5 h-3.5" />
                        {lead.company || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-secondary text-foreground text-xs">
                        {lead.source?.replace('form:', 'Form: ') || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                        <Calendar className="w-3 h-3" />
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(lead.id)}>
                        <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
